import { createBrowserClient } from '@supabase/ssr'
import { cleanSupabaseUrl } from './url'

const supabaseUrl = cleanSupabaseUrl(process.env.NEXT_PUBLIC_SUPABASE_URL)
const supabaseKey =
  process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('NEXT_PUBLIC_SUPABASE_URL is not set. Check your environment variables.')
}

if (!supabaseKey) {
  throw new Error(
    'NEXT_PUBLIC_SUPABASE_ANON_KEY or NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY must be set. Check your environment variables.'
  )
}

let _singleton: ReturnType<typeof createBrowserClient> | null = null

export function createClient() {
  if (_singleton) {
    return _singleton
  }
  const client = createBrowserClient(supabaseUrl as string, supabaseKey as string, {
    realtime: {
      worker: true,
    },
  })
  _singleton = client

  // Keep the Realtime WebSocket's JWT in sync with auth session refreshes.
  // Without this, the socket opens with the JWT present at first connection
  // and keeps using it forever. Once that token expires (Supabase default
  // 1h), every channel errors with CHANNEL_ERROR and never recovers.
  client.auth.onAuthStateChange((_event, session) => {
    const token = session?.access_token
    if (token) {
      client.realtime.setAuth(token)
    }
  })

  // Race the Supabase-internal visibilitychange reconnect with a fresh token.
  // Supabase Realtime reconnects all channels immediately on tab focus using
  // whatever JWT it last knew about. If that token is stale (>1h idle), every
  // channel CHANNEL_ERRORs before onAuthStateChange fires. Calling getSession()
  // here is fast (memory hit if valid, one refresh call if expired) and pushes
  // the new token to the socket at the same moment the reconnect fires.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        client.auth.getSession().then(({ data }) => {
          const token = data.session?.access_token
          if (token) {
            client.realtime.setAuth(token)
          }
        })
      }
    })
  }

  return client
}
